import { useRef, useCallback } from "react";
import type { QuizFormData } from "@core/features/quiz";
import { FitQuizLayout } from "./FitQuizLayout";
import { useAnonymousQuizNavigation, useGetAnonymousQuiz } from "@platform/features/programQuiz";
import { QuizQuestionContent, type QuizQuestionContentRef } from "./QuizQuestionContent";
import { fitQuizCopy } from "./fitQuizCopy";

type Props = {
  /** The quiz ID to fetch and display */
  quizId: number;
  /** Callback when quiz is completed with all collected answers */
  onComplete: (formData: QuizFormData) => void;
};

/**
 * Container component that combines FitQuizLayout with anonymous quiz functionality.
 * Handles data fetching, navigation state, and quiz completion. The heading,
 * intro and last button are the quiz's own, as written in admin (fitQuizCopy).
 */
export function FitQuizLayoutContainer({
  quizId,
  onComplete,
}: Props) {
  const quizContentRef = useRef<QuizQuestionContentRef>(null);

  const { data: quiz, isLoading, error } = useGetAnonymousQuiz({ quizId });
  const copy = fitQuizCopy(quiz);

  const {
    activeQuestionSet,
    buttonLabel,
    filterQuestionSet,
    handleNextQuestionObject,
    handlePreviousQuestionObject,
    isSubmitting,
    formData,
    stepIndex,
    totalSteps,
  } = useAnonymousQuizNavigation({
    quiz,
    onCompleteQuiz: onComplete,
    submitLabel: copy.submitLabel,
  });

  const handleButtonClick = useCallback(async () => {
    if (quizContentRef.current) {
      await quizContentRef.current.submit();
    }
  }, []);

  const isQuizLoading = isLoading || !!error || !quiz;

  return (
    <FitQuizLayout
      title={copy.title}
      description={copy.description}
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      buttonLabel={buttonLabel}
      isButtonDisabled={isSubmitting}
      onButtonClick={handleButtonClick}
      showBackButton={stepIndex > 0}
      onBackClick={handlePreviousQuestionObject}
      isLoading={isQuizLoading}
    >
      <QuizQuestionContent
        ref={quizContentRef}
        activeQuestionSet={activeQuestionSet}
        filterQuestionSet={filterQuestionSet}
        onSubmit={handleNextQuestionObject}
        formData={formData}
        stepIndex={stepIndex}
      />
    </FitQuizLayout>
  );
}
